import React, { useState, useEffect } from 'react';
import { Gift, Info } from 'lucide-react';

const CreditsCheckout = ({ userEmail, hoursToBook, originalPrice, onCreditsApplied }) => {
  const [userCredits, setUserCredits] = useState(null);
  const [creditsCalculation, setCreditsCalculation] = useState(null);
  const [useCredits, setUseCredits] = useState(false);
  const [loading, setLoading] = useState(true);

  const backendUrl = '';

  useEffect(() => {
    if (userEmail && hoursToBook) {
      fetchUserCredits();
      calculateCreditsDiscount();
    }
  }, [userEmail, hoursToBook]);

  const fetchUserCredits = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/credits/stats/${userEmail}`);
      const data = await response.json();
      setUserCredits(data);
    } catch (error) {
      console.error('Error fetching user credits:', error);
    }
  };

  const calculateCreditsDiscount = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/credits/calculate-booking-discount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          hours_to_book: hoursToBook
        })
      });
      const data = await response.json();
      setCreditsCalculation(data);
    } catch (error) {
      console.error('Error calculating credits:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (useCredits && creditsCalculation?.can_use_credits && onCreditsApplied) {
      const perHour = originalPrice / hoursToBook;
      const discount = creditsCalculation.hours_covered * perHour;
      onCreditsApplied({
        creditsUsed: creditsCalculation.credits_to_use,
        hoursCovered: creditsCalculation.hours_covered,
        hoursToPay: creditsCalculation.hours_to_pay,
        discountAmount: discount,
        finalPrice: originalPrice - discount
      });
    } else if (!useCredits && onCreditsApplied) {
      onCreditsApplied(null);
    }
  }, [useCredits, creditsCalculation]);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
        <div className="animate-pulse flex items-center gap-3">
          <div className="h-6 w-6 bg-purple-500/30 rounded"></div>
          <div className="h-6 w-48 bg-purple-500/30 rounded"></div>
        </div>
      </div>
    );
  }

  if (!creditsCalculation?.can_use_credits) {
    if (userCredits && userCredits.available > 0 && userCredits.available < 3) {
      return (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="text-yellow-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-yellow-200">
              <p className="font-semibold mb-1">Almost there!</p>
              <p>You have {userCredits.available} credits. You need 3 credits (1 hour) to start using them for bookings.</p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  const pricePerHour = originalPrice / hoursToBook;
  const discountAmount = creditsCalculation.hours_covered * pricePerHour;
  const finalPrice = originalPrice - (useCredits ? discountAmount : 0);

  return (
    <div className="space-y-4">
      {/* Credits Available Banner */}
      <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-2 border-purple-500/50 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-2 rounded-lg">
              <Gift className="text-white" size={24} />
            </div>
            <div>
              <p className="text-white font-bold text-lg">You Have {creditsCalculation.available_credits} Credits!</p>
              <p className="text-gray-300 text-sm">Worth {Math.floor(creditsCalculation.available_credits / 3)} hour{Math.floor(creditsCalculation.available_credits / 3) !== 1 ? 's' : ''} of studio time</p>
            </div>
          </div>
        </div>
      </div>

      {/* Use Credits Toggle */}
      <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={useCredits}
              onChange={(e) => setUseCredits(e.target.checked)}
              className="w-5 h-5 rounded border-2 border-purple-500 bg-transparent checked:bg-purple-600 focus:ring-2 focus:ring-purple-500 cursor-pointer"
            />
            <span className="text-white font-semibold text-lg">Use Credits for this Booking</span>
          </label>
        </div>

        {useCredits && (
          <div className="space-y-3 bg-black/30 rounded-lg p-4 border border-purple-500/20">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-300">Credits to use:</span>
              <span className="text-purple-300 font-bold">{creditsCalculation.credits_to_use} credits</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-300">Hours covered:</span>
              <span className="text-green-400 font-bold">{creditsCalculation.hours_covered} hour(s) FREE</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-300">Hours to pay:</span>
              <span className="text-blue-300 font-bold">{creditsCalculation.hours_to_pay} hour(s)</span>
            </div>
            <div className="border-t border-purple-500/30 pt-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Credits remaining after:</span>
                <span className="text-white font-bold">{creditsCalculation.remaining_credits} credits</span>
              </div>
            </div>
          </div>
        )}

        {/* Price Summary */}
        <div className="mt-6 space-y-2">
          <div className="flex justify-between items-center text-lg">
            <span className="text-gray-300">Original Price:</span>
            <span className={useCredits ? 'text-gray-500 line-through' : 'text-white font-bold'}>
              ${originalPrice}
            </span>
          </div>
          {useCredits && (
            <>
              <div className="flex justify-between items-center text-lg">
                <span className="text-green-400">Credits Discount:</span>
                <span className="text-green-400 font-bold">-${discountAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-2xl font-bold border-t border-purple-500/30 pt-3">
                <span className="text-white">Final Price:</span>
                <span className="text-white">
                  {finalPrice === 0 ? 'FREE!' : `$${finalPrice.toFixed(2)}`}
                </span>
              </div>
            </>
          )}
        </div>

        {useCredits && finalPrice === 0 && (
          <div className="mt-4 bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center">
            <p className="text-green-300 font-bold text-lg">🎉 This session is completely FREE with your credits!</p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-blue-200">
            <p className="font-semibold mb-1">How Credits Work</p>
            <ul className="space-y-1 text-blue-300">
              <li>• 3 credits = 1 hour of studio time</li>
              <li>• You can use partial credits (e.g., 7 credits = 2 hours + keep 1)</li>
              <li>• Minimum 3 credits required to redeem</li>
              <li>• Credits never expire!</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditsCheckout;